/**
 * Heart rate zone calculations and analysis.
 *
 * Zones use the classic 5-zone model based on a percentage of max HR:
 *   Z1  Recovery          50–60%
 *   Z2  Aerobic / easy    60–70%
 *   Z3  Tempo             70–80%
 *   Z4  Threshold         80–90%
 *   Z5  VO2 max / max     90–100%
 *
 * `maxHr` is estimated from the user's age (Tanaka et al. 2001:
 *   208 - 0.7 * age) when the user has not measured it manually. Apple Health
 * stores measured Max HR via vo2-max workout flows; we accept either source.
 */
export type HrZoneId = 'z1' | 'z2' | 'z3' | 'z4' | 'z5';

export interface HrZone {
  id: HrZoneId;
  label: string;
  description: string;
  lowerBpm: number;
  upperBpm: number;
  /** [0,1] inclusive lower bound of HRmax. */
  lowerPct: number;
  /** [0,1] exclusive upper bound (for Z5 = 1.0 inclusive). */
  upperPct: number;
  color: string;
}

export interface HrZoneBuckets {
  /** Total seconds the user spent in each zone. */
  secondsInZone: Record<HrZoneId, number>;
  /** Percentage [0,100] of the run spent in each zone (rounded to 1 dp). */
  percentInZone: Record<HrZoneId, number>;
  /** Total seconds with a sample available (used as denominator). */
  totalSeconds: number;
}

export interface HrSample {
  /** UNIX epoch in ms or seconds (we normalize). */
  timestamp: number;
  bpm: number;
}

/** Normalize HR sample timestamps to UNIX epoch milliseconds. */
export function normalizeHrTimestamp(ts: number): number {
  if (!Number.isFinite(ts) || ts <= 0) return ts;
  return ts > 1e12 ? Math.round(ts) : Math.round(ts * 1000);
}

export function normalizeHrSamples(samples: HrSample[]): HrSample[] {
  return samples.map((s) => ({
    ...s,
    timestamp: normalizeHrTimestamp(s.timestamp),
    bpm: s.bpm,
  }));
}

export type ZoneSecondsMap = { z1: number; z2: number; z3: number; z4: number; z5: number };

/** Build zone histogram buckets from watch-reported dwell seconds. */
/** Flat zone-id → seconds map for share templates (accepts nested HrZoneBuckets too). */
export function flattenHrBucketsForShare(
  buckets: HrZoneBuckets | Record<string, number> | null | undefined,
): Record<HrZoneId, number> {
  const empty: Record<HrZoneId, number> = { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 };
  if (!buckets) return empty;
  if (typeof buckets === 'object' && 'secondsInZone' in buckets && buckets.secondsInZone) {
    return { ...empty, ...buckets.secondsInZone };
  }
  const flat = buckets as Record<string, number>;
  return {
    z1: flat.z1 ?? 0,
    z2: flat.z2 ?? 0,
    z3: flat.z3 ?? 0,
    z4: flat.z4 ?? 0,
    z5: flat.z5 ?? 0,
  };
}

export function bucketsFromZoneSeconds(
  zoneSeconds: ZoneSecondsMap,
  zones: HrZone[],
): HrZoneBuckets {
  const secondsInZone: Record<HrZoneId, number> = {
    z1: Math.max(0, zoneSeconds.z1 ?? 0),
    z2: Math.max(0, zoneSeconds.z2 ?? 0),
    z3: Math.max(0, zoneSeconds.z3 ?? 0),
    z4: Math.max(0, zoneSeconds.z4 ?? 0),
    z5: Math.max(0, zoneSeconds.z5 ?? 0),
  };
  const totalSeconds = zones.reduce((sum, z) => sum + (secondsInZone[z.id] || 0), 0);
  const percentInZone: Record<HrZoneId, number> = { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 };
  if (totalSeconds > 0) {
    for (const z of zones) {
      percentInZone[z.id] =
        Math.round(((secondsInZone[z.id] || 0) / totalSeconds) * 1000) / 10;
    }
  }
  return { secondsInZone, percentInZone, totalSeconds };
}

export interface HrZoneSettings {
  maxHr: number;
  zones: HrZone[];
  source: 'measured' | 'estimated';
}

const ZONE_DEFINITIONS: Array<Omit<HrZone, 'lowerBpm' | 'upperBpm'>> = [
  {
    id: 'z1',
    label: 'Z1 Recovery',
    description: 'Very easy effort, conversational, breathing through nose.',
    lowerPct: 0.5,
    upperPct: 0.6,
    color: '#3b82f6', // blue
  },
  {
    id: 'z2',
    label: 'Z2 Aerobic',
    description: 'Easy long-run pace, full sentences possible.',
    lowerPct: 0.6,
    upperPct: 0.7,
    color: '#10b981', // green
  },
  {
    id: 'z3',
    label: 'Z3 Tempo',
    description: 'Comfortably hard, talking in short phrases only.',
    lowerPct: 0.7,
    upperPct: 0.8,
    color: '#f59e0b', // amber
  },
  {
    id: 'z4',
    label: 'Z4 Threshold',
    description: 'Hard. Just below lactate threshold, single words.',
    lowerPct: 0.8,
    upperPct: 0.9,
    color: '#f97316', // orange
  },
  {
    id: 'z5',
    label: 'Z5 VO2 max',
    description: 'All-out interval pace. Cannot be sustained for long.',
    lowerPct: 0.9,
    upperPct: 1.0,
    color: '#ef4444', // red
  },
];

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

/** Estimate max HR from age using Tanaka et al. 2001 formula. */
export function estimateMaxHr(ageYears: number): number {
  if (!Number.isFinite(ageYears) || ageYears <= 0) return 190; // conservative default
  return Math.round(208 - 0.7 * clamp(ageYears, 10, 100));
}

/** Build the zone table for a given max HR. */
export function buildHrZones(opts: {
  maxHr?: number | null;
  ageYears?: number | null;
}): HrZoneSettings {
  const measured = typeof opts.maxHr === 'number' && opts.maxHr >= 120 && opts.maxHr <= 230
    ? Math.round(opts.maxHr)
    : null;
  const maxHr = measured ?? estimateMaxHr(opts.ageYears ?? 30);
  const zones: HrZone[] = ZONE_DEFINITIONS.map((def) => ({
    ...def,
    lowerBpm: Math.round(maxHr * def.lowerPct),
    upperBpm: Math.round(maxHr * def.upperPct),
  }));
  return {
    maxHr,
    zones,
    source: measured !== null ? 'measured' : 'estimated',
  };
}

/** Classify a single bpm value into its zone (or null if no data). Below Z1 → Z1. */
export function classifyBpm(bpm: number, zones: HrZone[]): HrZoneId | null {
  if (!Number.isFinite(bpm) || bpm <= 0) return null;
  for (const z of zones) {
    if (bpm >= z.lowerBpm && bpm < z.upperBpm) return z.id;
  }
  const last = zones[zones.length - 1];
  if (last && bpm >= last.upperBpm) return last.id;
  const first = zones[0];
  if (first && bpm < first.lowerBpm) return first.id;
  return null;
}

/**
 * Walk through a sorted HR sample stream and bucket dwell time per zone.
 * Uses the time gap between consecutive samples as the dwell value, capped
 * at `maxGapSeconds` so a long silence doesn't bias the histogram.
 */
export function bucketHrZones(
  samples: HrSample[],
  zones: HrZone[],
  options?: { maxGapSeconds?: number },
): HrZoneBuckets {
  const out: HrZoneBuckets = {
    secondsInZone: { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 },
    percentInZone: { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 },
    totalSeconds: 0,
  };
  if (samples.length === 0) return out;

  const maxGapSec = options?.maxGapSeconds ?? 30;
  const sorted = normalizeHrSamples(samples).sort((a, b) => a.timestamp - b.timestamp);

  for (let i = 0; i < sorted.length; i++) {
    const cur = sorted[i];
    const next = sorted[i + 1];
    const tsCur = cur.timestamp;
    const tsNext = next ? next.timestamp : tsCur + 5_000;
    const dwellSec = clamp(Math.round((tsNext - tsCur) / 1000), 1, maxGapSec);
    const zone = classifyBpm(cur.bpm, zones);
    if (zone) {
      out.secondsInZone[zone] += dwellSec;
      out.totalSeconds += dwellSec;
    }
  }

  if (out.totalSeconds > 0) {
    for (const z of zones) {
      out.percentInZone[z.id] =
        Math.round((out.secondsInZone[z.id] / out.totalSeconds) * 1000) / 10;
    }
  }
  return out;
}

export type PhaseHrMetrics = {
  sampleCount: number;
  avgBpm: number | null;
  secondsInTargetZone: number;
  targetZonePct: number;
  secondsInZone: Record<HrZoneId, number>;
  dominantZone: HrZoneId | null;
  totalTrackedSeconds: number;
};

/** Bucket HR dwell time for one workout phase using elapsed-second boundaries. */
export function bucketHrZonesForElapsedWindow(
  samples: HrSample[],
  zones: HrZone[],
  runStartMs: number,
  startElapsedSec: number,
  endElapsedSec: number,
  targetZone?: HrZoneId | null,
  options?: { maxGapSeconds?: number; minTrackedSeconds?: number },
): PhaseHrMetrics | null {
  if (samples.length === 0 || !Number.isFinite(runStartMs) || endElapsedSec <= startElapsedSec) {
    return null;
  }

  const startMs = runStartMs + Math.max(0, startElapsedSec) * 1000;
  const endMs = runStartMs + endElapsedSec * 1000;
  const inWindow = normalizeHrSamples(samples).filter(
    (s) => s.timestamp >= startMs - 2000 && s.timestamp <= endMs + 2000,
  );
  if (inWindow.length === 0) return null;

  const buckets = bucketHrZones(inWindow, zones, options);
  const minTracked = options?.minTrackedSeconds ?? 15;
  if (buckets.totalSeconds < minTracked) return null;

  const avgBpm = Math.round(inWindow.reduce((sum, s) => sum + s.bpm, 0) / inWindow.length);
  const secondsInTargetZone = targetZone ? buckets.secondsInZone[targetZone] ?? 0 : 0;
  const targetZonePct =
    buckets.totalSeconds > 0
      ? Math.round((secondsInTargetZone / buckets.totalSeconds) * 100)
      : 0;

  let dominantZone: HrZoneId | null = null;
  let maxSec = 0;
  for (const z of zones) {
    const sec = buckets.secondsInZone[z.id] ?? 0;
    if (sec > maxSec) {
      maxSec = sec;
      dominantZone = z.id;
    }
  }

  return {
    sampleCount: inWindow.length,
    avgBpm,
    secondsInTargetZone,
    targetZonePct,
    secondsInZone: buckets.secondsInZone,
    dominantZone,
    totalTrackedSeconds: buckets.totalSeconds,
  };
}

/** Live-zone helper for the voice coach. Returns the current zone + cue. */
export function buildHrZoneVoiceCue(
  currentBpm: number,
  zones: HrZone[],
  targetZone?: HrZoneId | null,
): { zone: HrZoneId | null; message: string } | null {
  const zone = classifyBpm(currentBpm, zones);
  if (!zone) return null;
  const z = zones.find((x) => x.id === zone);
  if (!z) return null;

  if (targetZone && zone !== targetZone) {
    const target = zones.find((x) => x.id === targetZone);
    if (!target) return null;
    if (zone === 'z5' || zones.findIndex((x) => x.id === zone) > zones.findIndex((x) => x.id === targetZone)) {
      return {
        zone,
        message: `Heart rate is in ${z.label}. Ease back to ${target.label}, around ${target.lowerBpm} to ${target.upperBpm} beats.`,
      };
    }
    return {
      zone,
      message: `You are in ${z.label}. Lift the effort a little to reach ${target.label}, around ${target.lowerBpm} to ${target.upperBpm} beats.`,
    };
  }

  return {
    zone,
    message: `Holding ${z.label} at ${Math.round(currentBpm)} beats per minute. Good work.`,
  };
}

const formatZoneDuration = (sec: number): string => {
  if (!Number.isFinite(sec) || sec <= 0) return '0s';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m}m`;
  return `${m}m ${s}s`;
};

/** Text block for Arnold / chat — zone ranges, time in zone, and averages. */
export function formatHeartRateSummaryForCoach(
  hr:
    | {
        maxHr?: number;
        maxHrSource?: 'measured' | 'estimated';
        avgBpm?: number | null;
        minBpm?: number | null;
        peakBpm?: number | null;
        targetZone?: HrZoneId | null;
        zones?: HrZone[];
        buckets?: HrZoneBuckets;
        sampleCount?: number;
      }
    | null
    | undefined,
): string {
  if (!hr) return '';

  const lines: string[] = [];
  if (hr.maxHr) {
    lines.push(
      `Max HR reference: ${hr.maxHr} bpm${hr.maxHrSource === 'estimated' ? ' (age-estimated)' : ''}`,
    );
  }
  if (hr.avgBpm != null) lines.push(`Average HR: ${Math.round(hr.avgBpm)} bpm`);
  if (hr.minBpm != null) lines.push(`Minimum HR: ${Math.round(hr.minBpm)} bpm`);
  if (hr.peakBpm != null) lines.push(`Peak HR: ${Math.round(hr.peakBpm)} bpm`);
  if (hr.targetZone) lines.push(`Planned target zone for this run: ${hr.targetZone.toUpperCase()}`);
  if (hr.sampleCount != null && hr.sampleCount > 0) {
    lines.push(`HR samples during run: ${hr.sampleCount}`);
  }

  if (hr.zones?.length && hr.buckets) {
    lines.push('Time in each zone:');
    for (const id of ['z1', 'z2', 'z3', 'z4', 'z5'] as HrZoneId[]) {
      const z = hr.zones.find((x) => x.id === id);
      const sec = hr.buckets.secondsInZone[id] || 0;
      const pct = hr.buckets.percentInZone[id] || 0;
      const range = z ? `${z.lowerBpm}-${z.upperBpm} bpm` : '';
      lines.push(
        `  ${id.toUpperCase()}${range ? ` (${range})` : ''}: ${formatZoneDuration(sec)} — ${pct.toFixed(1)}% of tracked time`,
      );
    }
    if (hr.buckets.totalSeconds > 0) {
      lines.push(`Total HR-tracked time: ${formatZoneDuration(hr.buckets.totalSeconds)}`);
    }
  } else if (hr.avgBpm != null && hr.zones?.length) {
    const zone = classifyBpm(hr.avgBpm, hr.zones);
    if (zone) {
      const z = hr.zones.find((x) => x.id === zone);
      lines.push(
        `Estimated effort zone from average HR: ${zone.toUpperCase()}${z ? ` (${z.lowerBpm}-${z.upperBpm} bpm)` : ''}`,
      );
    }
  }

  if (lines.length === 0) return '';
  return `\n\nHEART-RATE ZONES (Apple Health — keep this profile for future run prescriptions):\n${lines.join('\n')}`;
}
